import Analytics from '@/utils/analytics';
import CheckmarkIcon from '@assets/icons/checkmark.svg';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import {
  Insets,
  StyleProp,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { SvgProps } from 'react-native-svg';
import { TrackingData } from '@/utils/analytics/base-reporter';

type StyleFn = (isSelected: boolean) => StyleProp<ViewStyle>;

interface Props {
  accessibilityLabel?: string;
  checkProps?: SvgProps;
  disabled?: boolean;
  getCheckboxStyle?: StyleFn;
  hitSlop?: Insets;
  icon?: React.ReactNode;
  isSelected: boolean;
  label?: string;
  labelStyle?: StyleProp<TextStyle>;
  onSelect: () => void;
  style?: StyleProp<ViewStyle>;
  trackingData: TrackingData | null;
}

export const DEFAULT_CHECKBOX_SIZE = 22;
export const CHECKBOX_HORIZONTAL_PADDING = 16;

// default to padding applied to TouchableOpacity style
const DEFAULT_HIT_SLOP = {
  bottom: 0,
  left: 0,
  right: 0,
  top: 0,
};

export const Checkbox: React.FC<Props> = ({
  accessibilityLabel,
  checkProps,
  disabled,
  getCheckboxStyle,
  hitSlop = DEFAULT_HIT_SLOP,
  icon,
  isSelected,
  label,
  labelStyle,
  onSelect,
  style,
  trackingData,
}) => {
  const [styles, theme] = useStyles();
  return (
    <TouchableOpacity
      accessibilityLabel={accessibilityLabel}
      disabled={disabled}
      hitSlop={hitSlop}
      onPress={() => {
        if (trackingData) {
          Analytics.track(
            `${trackingData.name}.checkbox_press`,
            trackingData.properties,
          );
        }

        onSelect();
      }}
      style={[styles.base, label ? styles.baseWithLabel : null, style]}
      testID={`checkbox-${isSelected ? '' : 'un'}checked`}
    >
      <View
        style={[
          styles.checkbox,
          disabled ? styles.checkboxDisabled : null,
          getCheckboxStyle?.(isSelected),
        ]}
      >
        {isSelected
          ? icon || (
              <CheckmarkIcon
                height={18}
                stroke={theme.colors.primary}
                width={18}
                {...checkProps}
              />
            )
          : null}
      </View>
      {label ? (
        <Text
          style={[
            TYPOGRAPHY.small,
            FONTS.primarySemiBold,
            styles.label,
            disabled ? styles.labelDisabled : null,
            labelStyle,
          ]}
        >
          {label}
        </Text>
      ) : null}
    </TouchableOpacity>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    backgroundColor: theme.colors.white,
    paddingHorizontal: CHECKBOX_HORIZONTAL_PADDING,
    paddingVertical: 28,
  },
  baseWithLabel: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  checkbox: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.inactiveFill,
    borderRadius: 4,
    borderWidth: 2,
    height: DEFAULT_CHECKBOX_SIZE,
    justifyContent: 'center',
    width: DEFAULT_CHECKBOX_SIZE,
  },
  checkboxDisabled: {
    borderColor: theme.sk.color.border.button.disabled,
  },
  label: {
    ...FONTS.primarySemiBold,
    marginHorizontal: 8,
  },
  labelDisabled: {
    color: theme.sk.color.text.disabled,
  },
}));
